// longfight — map lab. Standalone visualizer for the procedural level generator:
// generate -> validate -> mesh -> inspect (orbit / fly), with a live minimap + stats.

import * as THREE from "three";
import { CAMERA, DEFAULT_SEED, PLAYER, topY } from "./config";
import { Input } from "./engine/input";
import { GRADE_DEFAULTS, LUTS } from "./world/grade";
import { Scene, FACTORY_BAND, UNITS_BAND } from "./world/scene";
import { CameraRig } from "./world/camera";
import { generateLevel } from "./map/grammar";
import { validateLevel } from "./map/validate";
import { buildLevelMesh, disposeGroup } from "./map/mesh";
import { Minimap } from "./ui/minimap";
import { formatStats, type PanelState } from "./ui/panel";
import { Army } from "./entities/army";
import { Tanks } from "./entities/tanks";
import { Projectiles } from "./systems/projectiles";
import { HealthBars } from "./systems/healthbars";
import { sfx } from "./engine/sfx";
import { music } from "./engine/music";
import { NowPlaying } from "./ui/nowplaying";
import { Player } from "./entities/player";
import { PlayerHud } from "./ui/playerhud";
import { PlayerCombat } from "./systems/playercombat";
import { UnitBank } from "./systems/economy";
import { balance } from "./systems/balance";
import type { WallBox } from "./systems/shop";
import type { NavGraph } from "./systems/pathfind";
import { writeBattleResult } from "./campaign/run";
import { Atmosphere, resolveBiome, sunDir, PERIMETER_COLOR, type Biome } from "./env/atmosphere";
import { cloudParamsForBiome, type CloudParams } from "./env/clouds";
import { installAerialFog, patchSceneFog, setFogStorm, getFogParams, fogUniforms, FOG_DENSITY_UNIT } from "./env/fog";
import { DevPanel } from "./ui/devpanel";
import { Perimeter } from "./env/perimeter";
import { buildTerrain, type Terrain } from "./env/terrain";
import { generateTerra, type TerraLevel } from "./terra/field";
import { buildTerraMesh, type TerraMeshResult } from "./terra/mesh";
import { Hazards } from "./systems/hazards";
import type { GeneratedLevel } from "./map/grammar";
import { Factory } from "./factory/factory";
import { PHYS } from "./factory/defs";

// ---- campaign integration: this game doubles as the battle for a UMa3 campaign node when launched with
// ?campaign=1 (see src/campaign). Reaching the enemy core wins the op; too many deaths loses it; either
// way we write the result to localStorage and return to campaign.html. All of it is gated behind params
// so the standalone sandbox (no query string) behaves exactly as before. ----
const CORE_WIN_R = 260;        // reach within this of the enemy core (studs) => campaign victory
const CAMPAIGN_MAX_DEATHS = 3; // deaths before the op is a failure

interface CampaignParams {
  nodeId: number; tier: number; seed: string; label: string; kind: string;
  zone: "hot" | "temperate" | "cold"; ptype: string; ptemp: number; garden: boolean; starHex: string;
  sky: string; // destination horizon color (6 hex) — the landing wash we open from
  sunEl: number | null; // REAL local sun elevation/azimuth at the landing site (deg), from the
  sunAz: number | null; // deploy-time orbital geometry — null = use the biome's art default
  dev: boolean; // launched from the campaign's DEV land-any-world picker → return to the map, not the intro
}
function readCampaignParams(): CampaignParams | null {
  const q = new URLSearchParams(location.search);
  if (q.get("campaign") !== "1") return null;
  const zone = q.get("zone");
  return {
    nodeId: Number(q.get("node")) || 0,
    tier: Number(q.get("tier")) || 1,
    seed: q.get("seed") || "uma3",
    label: q.get("label") || "OBJECTIVE",
    kind: q.get("kind") || "battle",
    zone: zone === "hot" || zone === "cold" ? zone : "temperate",
    ptype: q.get("ptype") || "ROCKY",
    ptemp: Number(q.get("ptemp")) || 250,
    garden: q.get("garden") === "1",
    starHex: q.get("star") || "ffb060",
    sky: q.get("sky") || "",
    sunEl: q.has("sunel") ? Number(q.get("sunel")) : null,
    sunAz: q.has("sunaz") ? Number(q.get("sunaz")) : null,
    dev: q.get("dev") === "1",
  };
}

class MapLab {
  private scene: Scene;
  private input: Input;
  private rig: CameraRig;
  private minimap: Minimap;
  private panelState!: PanelState; // seed/level/toggles (drives regenerate); mutated by the DEV Level bank
  private statsOut: ((text: string, color?: string) => void) | null = null; // DEV Stats readout (set after build)
  private lastStats: Parameters<typeof formatStats> | null = null;           // cached so the first (pre-build) stats still show
  private setEconomy: (blue: number, red: number) => void = () => {};
  private army: Army;
  private tanks: Tanks;
  private projectiles: Projectiles;
  private healthBars: HealthBars;
  private factories: Factory[] = []; // base-building: [0]=player (blue, click-to-build), [1]=AI (red, procedural)

  // first-person player (press P to drop in; Esc to leave)
  private player: Player;
  private playerHud: PlayerHud;
  private playerCombat: PlayerCombat;
  private nav: NavGraph | null = null;
  private playing = false;
  private awaitingLock = false; // waiting for pointer lock right after entering play
  private playEnterAt = 0; // timestamp of enterPlay — backstop to bail if the lock never grants
  private playerPrevX = 0;
  private playerPrevZ = 0;
  private shopWalls: WallBox[] = []; // shop-building wall AABBs for the player collider
  private shopFootprint: WallBox | null = null; // shop floor rect (off the NPC nav) the player stands on
  private shopFloorY = 0;
  private playerStart: THREE.Vector3 | null = null; // spawn just outside the shop door
  private bbox = { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
  private banks = [new UnitBank(), new UnitBank()]; // per-team Unit-Point pools (blue, red)

  // campaign op (null in standalone sandbox mode)
  private campaign = readCampaignParams();
  private campaignResolved = false;
  private campaignDeaths = 0;
  private campaignBanner: HTMLDivElement | null = null;
  private biome: Biome | null = null;
  private atmosphere: Atmosphere | null = null;
  private cloud: CloudParams | null = null; // resolved once per battle; drives sky + ground cloud drift
  private cloudDriftMul = 1; // DEV-tunable multiplier on cloud drift speed (sky + shadows stay in sync)
  private cloudPhase = 0; // integrated drift seconds — integrating (not scaling nowSec) keeps live speed tweaks smooth
  private dev: DevPanel | null = null; // collapsible dev controls (LOD knobs + future per-feature banks)
  private debugLod = 0; // terra LOD viz mode: 0 off, 1 raw px footprint bands, 2 effective detail heatmap
  private perfOut: ((text: string) => void) | null = null; // DEV Performance readout (GPU/CPU/fps)
  private perfFrames = 0; private perfDt = 0; private perfCpu = 0; private perfLast = 0;
  // dynamic resolution — scales the 3D render buffer (never the UI) to hold GPU under budget for steady 60fps
  private drs = true; private manualScale = 1;
  private drsMin = 0.5; private drsMax = 2.0; private drsTargetMs = 13; private drsLast = 0;
  private readonly _focus = { x: 0, z: 0 }; // per-frame scratch for the audio/sky/sun/minimap focus point
  private terrain: Terrain | null = null;
  private perimeter: Perimeter | null = null;
  private sprawl: THREE.Group[] = [];
  // TERRA (campaign battles): open sculpted battlefield replacing the tile maze (the sandbox lab keeps tiles)
  private terraLevel: TerraLevel | null = null;
  private terraMesh: TerraMeshResult | null = null;
  private hazards = new Hazards();
  private frameRect: { minX: number; maxX: number; minZ: number; maxZ: number } | null = null;

  private group: THREE.Group | null = null;

  private lastTime = performance.now();

  constructor(container: HTMLElement) {
    installAerialFog(); // override three's fog chunks globally BEFORE any material compiles
    this.scene = new Scene(container);
    this.input = new Input(this.scene.domElement);
    this.rig = new CameraRig(this.scene.camera, this.input);
    this.army = new Army(this.scene.scene);
    this.army.setCamera(this.scene.camera); // lets the army frustum-cull off-screen giants
    void this.army.load();
    this.tanks = new Tanks(this.scene.scene);
    this.tanks.setArmy(this.army);
    void this.tanks.load();
    // one shared projectile system both sides fire into (a soldier's rocket can kill a tank)
    this.projectiles = new Projectiles(this.scene.scene);
    this.projectiles.addSide(this.army);
    this.projectiles.addSide(this.tanks);
    this.army.setProjectiles(this.projectiles);
    this.tanks.setProjectiles(this.projectiles);
    this.army.addTargetSide(this.tanks); // ranged soldiers may also target tanks
    // health bars for the heavy units (tanks + giants)
    this.healthBars = new HealthBars(this.scene.scene);
    this.army.setHealthBars(this.healthBars);
    this.tanks.setHealthBars(this.healthBars);
    // shared per-team Unit-Point banks: soldiers, giants, and tanks all spend from these
    this.army.setBanks(this.banks);
    this.tanks.setBanks(this.banks);

    // in a campaign op the node dictates the seed + difficulty; otherwise the sandbox default
    const initial: PanelState = this.campaign
      ? { seed: this.campaign.seed, level: Math.max(1, this.campaign.tier), curbs: true, grid: false, armies: true, tanks: true }
      : { seed: DEFAULT_SEED, level: 1, curbs: true, grid: true, armies: true, tanks: true };
    this.panelState = initial;
    this.minimap = new Minimap(container);

    // background music: a shuffle of the wiiplaytanks tracker set, streamed one at a time, with a
    // "now playing" credit toast. Begins on the first user gesture (same one that unlocks the sfx).
    const nowPlaying = new NowPlaying(container);
    music.onNowPlaying = (t) => nowPlaying.show(t);
    music.start();

    // the playable first-person soldier — camera is its eyes; collision samples the level nav
    this.player = new Player(this.scene.camera);
    this.playerHud = new PlayerHud(container);
    // weapons + shop + making the player a combatant the red army/tanks target
    this.playerCombat = new PlayerCombat(
      this.scene.scene, this.scene.camera, this.player,
      this.army, this.tanks, this.projectiles, this.input, this.playerHud,
    );

    if (this.campaign) {
      // theme the battlefield as the target planet's surface (sky/sun/fog/lighting + temperature VFX)
      this.biome = resolveBiome({
        zone: this.campaign.zone, ptype: this.campaign.ptype, ptemp: this.campaign.ptemp,
        garden: this.campaign.garden, starHex: this.campaign.starHex,
      });
      this.cloud = cloudParamsForBiome(this.biome.key);
      // REAL sun position from the deploy-time orbital geometry (clamped: below ~18° the shadow
      // frustum stretches badly; above ~78° shadows lose all shape). Falls back to the biome default.
      const sunOverride = this.campaign.sunEl != null && Number.isFinite(this.campaign.sunEl)
        ? sunDir(Math.min(78, Math.max(18, this.campaign.sunEl)), this.campaign.sunAz ?? 37)
        : undefined;
      this.atmosphere = new Atmosphere(this.biome, sunOverride);
      this.atmosphere.install(this.scene);
      this.scene.registerCostProbe("sky", this.atmosphere.getDome()); // per-sector GPU cost
      this.scene.setShadowSoftGround(); // normal-biased shadows for the smooth open terrain
    }
    // base-building: the factory REPLACES auto-spawn — both armies are MANUFACTURED by assemblers.
    // Blue is player-built (click-to-place); red is AI-built (a procedural factory that lays itself out).
    this.army.autoSpawn = false;
    this.tanks.autoSpawn = false;
    const deploy = (team: 0 | 1, kind: "little" | "giant"): void => this.army.deploy(team, kind);
    this.factories = [
      new Factory(this.scene, this.input, container, { team: 0, mode: "player", deploy }),
      new Factory(this.scene, this.input, container, { team: 1, mode: "ai", deploy }),
    ];
    this.scene.registerCostProbe("ore", this.factories[0].oreMesh); // player factory's belt-item instanced mesh
    this.scene.registerCostBand("factory", FACTORY_BAND); // building draws (both factories, sentinel-bracketed) — created ONCE here
    this.scene.registerCostBand("units", UNITS_BAND);     // army little + giant draws
    this.scene.registerReflectionChannel();               // water planar-reflection nested render

    this.regenerate(initial); // builds the level + (campaign) the surrounding terrain / perimeter / sprawl
    if (this.campaign) this.buildCampaignBanner(container);
    this.buildDevPanel(container); // the single control menu: level/balance/music/stats + (campaign) terrain
    if (this.campaign) this.playArrival(container); // open FROM the descent's sky wash, settle onto the field
    requestAnimationFrame(this.frame);
  }

  /** The battle end of the orbit→surface "landing": the page opens fully covered by the destination's sky
   *  color (matching the campaign descent's wash — the navigation + level build happen hidden behind it),
   *  then the cover lifts while the camera settles down onto the freshly-generated battlefield. */
  private playArrival(container: HTMLElement): void {
    const p = this.campaign;
    const hex =
      p && /^[0-9a-fA-F]{6}$/.test(p.sky)
        ? "#" + p.sky
        : this.biome
          ? "#" + ((this.biome.preset.horizon >>> 0).toString(16).padStart(6, "0"))
          : "#0a1020";
    const o = document.createElement("div");
    o.style.cssText =
      "position:fixed;inset:0;z-index:100000;pointer-events:none;opacity:1;transition:opacity 1.6s ease-out;background:" + hex + ";";
    container.appendChild(o);
    // let the battlefield draw a couple of frames behind the cover, then lift it + start the camera descent
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.rig.arrive();
        o.style.opacity = "0";
        setTimeout(() => o.remove(), 1800);
      }),
    );
  }

  // ---- campaign op wiring (only active when launched with ?campaign=1) ----------------------------
  private corePoint(): { x: number; z: number } | null {
    // terra: the win zone is the PAD CENTER (where the core object stands) — coreKeys[0] is an
    // arbitrary corner cell up to ~1000 studs from the visible objective (review finding)
    if (this.terraLevel) return { x: this.terraLevel.exit.x, z: this.terraLevel.exit.z };
    const nav = this.nav;
    const k = nav ? nav.coreKeys[0] : undefined;
    const p = k && nav ? nav.point.get(k) : null;
    return p ? { x: p.x, z: p.z } : null;
  }

  private checkCampaignObjective(): void {
    if (!this.campaign || this.campaignResolved || !this.playing) return;
    const core = this.corePoint();
    if (!core) return;
    const dx = this.player.position.x - core.x, dz = this.player.position.z - core.z;
    if (dx * dx + dz * dz < CORE_WIN_R * CORE_WIN_R) this.campaignFinish(true);
  }

  // DEV excursions return to the overview (?noauto) so the fresh-run opening cinematic doesn't auto-fire
  // and commit the real tutorial; normal battles return to the map unmarked.
  private campaignReturnUrl(): string {
    return this.campaign?.dev ? "/campaign.html?noauto" : "/campaign.html";
  }

  private campaignFinish(win: boolean): void {
    if (!this.campaign || this.campaignResolved) return;
    this.campaignResolved = true;
    writeBattleResult({ nodeId: this.campaign.nodeId, win });
    window.location.href = this.campaignReturnUrl();
  }

  private campaignAbort(): void {
    if (this.campaignResolved) return;
    this.campaignResolved = true; // no result written → the node stays available to retry
    window.location.href = this.campaignReturnUrl();
  }

  private buildCampaignBanner(container: HTMLElement): void {
    const b = document.createElement("div");
    b.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:50;display:flex;gap:16px;align-items:center;" +
      "justify-content:center;padding:8px 14px;font-family:'Space Mono',ui-monospace,monospace;font-size:12px;" +
      "letter-spacing:0.12em;color:#3fd0e6;background:linear-gradient(180deg,rgba(5,12,16,0.92),rgba(5,12,16,0));" +
      "text-transform:uppercase;pointer-events:none;";
    const btn = "pointer-events:auto;background:transparent;font-family:inherit;font-size:11px;letter-spacing:0.14em;" +
      "padding:4px 12px;cursor:pointer;text-transform:uppercase;";
    b.innerHTML = `<span style="color:#f2762e;font-weight:700">◆ CAMPAIGN OP</span>
      <span id="cmp-op-label"></span>
      <span style="color:#5c7f88">— reach the enemy core —</span>
      <span id="cmp-op-losses" style="color:#5c7f88"></span>
      <button id="cmp-op-deploy" style="${btn}border:1px solid #3fd0e6;color:#3fd0e6">P · DEPLOY</button>
      <button id="cmp-op-abort" style="${btn}border:1px solid #f2762e;color:#f2762e">ABORT</button>`;
    container.appendChild(b);
    this.campaignBanner = b;
    (b.querySelector("#cmp-op-deploy") as HTMLButtonElement).onclick = () => this.enterPlay();
    (b.querySelector("#cmp-op-abort") as HTMLButtonElement).onclick = () => this.campaignAbort();
    this.updateCampaignBanner();
  }

  private updateCampaignBanner(): void {
    if (!this.campaign || !this.campaignBanner) return;
    const lab = this.campaignBanner.querySelector("#cmp-op-label");
    const los = this.campaignBanner.querySelector("#cmp-op-losses");
    if (lab) lab.textContent = `${this.campaign.label} · TIER ${this.campaign.tier}`;
    if (los) los.textContent = `losses ${this.campaignDeaths}/${CAMPAIGN_MAX_DEATHS}`;
  }

  /** THE control menu — collapsible, categorized banks (the old left panel folds in here). Standard
   *  practice: give every feature a bank. Slider targets that get REBUILT (terra material on regenerate)
   *  are re-pushed via `dev.applyAll()` at the end of `regenerate`. */
  private buildDevPanel(container: HTMLElement): void {
    const dev = new DevPanel(container);
    const regen = (): void => this.regenerate(this.panelState);

    // ---- Level (seed / difficulty / toggles) ----
    const lvl = dev.addBank("Level");
    const seedInput = lvl.addText("seed", this.panelState.seed, (v) => { this.panelState.seed = v.trim() || "longfight"; regen(); }, {
      label: "rnd", onClick: () => { this.panelState.seed = Math.random().toString(36).slice(2, 8); seedInput.value = this.panelState.seed; regen(); },
    });
    lvl.addStepper("level", this.panelState.level, (v) => { this.panelState.level = Math.max(1, v); regen(); }, { min: 1 });
    lvl.addToggles([
      { label: "curbs", value: this.panelState.curbs, onChange: (v) => { this.panelState.curbs = v; regen(); } },
      { label: "grid", value: this.panelState.grid, onChange: (v) => { this.panelState.grid = v; regen(); } },
      { label: "armies", value: this.panelState.armies, onChange: (v) => { this.panelState.armies = v; regen(); } },
      { label: "tanks", value: this.panelState.tanks, onChange: (v) => { this.panelState.tanks = v; regen(); } },
    ]);
    lvl.addButtons([
      { label: "regenerate", onClick: regen },
      { label: "verify sweep", onClick: () => runSweep() },
    ]);

    // ---- Physics (ore feel: shape, grip, how hard a belt corners). Most of these are baked into the rigid
    //      bodies, so changing one rebuilds both factories' physics worlds — ore keep their positions. ----
    const phy = dev.addBank("Physics", false);
    let physT = 0;
    const rebuildPhys = (): void => { // debounced: sliders fire continuously while dragging
      clearTimeout(physT);
      physT = window.setTimeout(() => { for (const f of this.factories) f.rebuildPhysics(); }, 220);
    };
    const shapeOut = phy.addReadout();
    const showShape = (): void => shapeOut(`shape: ${["box (catches corners)", "rounded box", "sphere (rolls)"][PHYS.shape]}`);
    phy.addButtons([
      { label: "box", onClick: () => { PHYS.shape = 0; showShape(); rebuildPhys(); } },
      { label: "rounded", onClick: () => { PHYS.shape = 1; showShape(); rebuildPhys(); } },
      { label: "sphere", onClick: () => { PHYS.shape = 2; showShape(); rebuildPhys(); } },
    ]);
    showShape();
    phy.addToggles([
      { label: "tumble (roll + tip)", value: PHYS.tumble, onChange: (v) => { PHYS.tumble = v; rebuildPhys(); } },
      // OFF is the good setting — a linear cast clamps ore to whatever their sweep touches, which cost ~85% of
      // all ore lost. Roblox steps discretely too. On only to check tunnelling of very fast flung ore.
      { label: "CCD (linear cast)", value: PHYS.ccd, onChange: (v) => { PHYS.ccd = v; rebuildPhys(); } },
    ]);
    // steer is the one knob that needs no rebuild — and the one that's least "pure physics": 0 = friction only,
    // which is honest but lets a 90° corner grab an ore by its leading edge and spit it out riding the seam.
    phy.addSlider({ label: "belt steer (0=pure friction)", min: 0, max: 0.6, step: 0.01, value: PHYS.steer, onChange: (v) => (PHYS.steer = v) });
    phy.addSlider({ label: "ore size", min: 4, max: 26, step: 1, value: PHYS.half, onChange: (v) => { PHYS.half = v; rebuildPhys(); } });
    phy.addSlider({ label: "belt speed", min: 30, max: 320, step: 10, value: PHYS.speed, onChange: (v) => { PHYS.speed = v; rebuildPhys(); } });
    phy.addSlider({ label: "friction (grip)", min: 0, max: 2, step: 0.05, value: PHYS.friction, onChange: (v) => { PHYS.friction = v; rebuildPhys(); } });
    phy.addSlider({ label: "bounce", min: 0, max: 0.8, step: 0.05, value: PHYS.restitution, onChange: (v) => { PHYS.restitution = v; rebuildPhys(); } });
    phy.addSlider({ label: "gravity", min: 300, max: 3000, step: 100, value: PHYS.gravity, onChange: (v) => { PHYS.gravity = v; rebuildPhys(); } });
    // Widening lanes so they overlap DOES keep cornering ore supported — but the ore then rides off-lane and the
    // grid-addressed economy (which reads its center tile) stops seeing it. Exposed to feel that trade directly.
    phy.addSlider({ label: "belt overhang", min: 0, max: 30, step: 2, value: PHYS.beltPad, onChange: (v) => { PHYS.beltPad = v; rebuildPhys(); } });
    // flat run-out at a ramp's top. At 0, ore reach the crest still climbing, wedge into an arch across the
    // lane and stop dead until the queue behind collapses it. Any value ≥0.10 removes the arch outright.
    phy.addSlider({ label: "ramp crest", min: 0, max: 0.6, step: 0.05, value: PHYS.rampCrest, onChange: (v) => { PHYS.rampCrest = v; rebuildPhys(); } });
    // how much a wall/curb's surface runs with the belt beside it. 0 = the barrier brakes ore sliding along it
    // (they can stop dead against a wall the belt is pushing them PARALLEL to); 1 = it drags them along.
    phy.addSlider({ label: "rail assist", min: 0, max: 1, step: 0.1, value: PHYS.railAssist, onChange: (v) => { PHYS.railAssist = v; } });
    const oreOut = phy.addReadout(true);
    phy.addButtons([
      { label: "rebuild now", onClick: () => { for (const f of this.factories) f.rebuildPhysics(); } },
      { label: "count ore", onClick: () => oreOut(this.factories.map((f, i) => `f${i}: ${f.oreCount()} ore`).join("  ")) },
    ]);

    // ---- Ore inspector: click an ore that LOOKS stuck and follow that one ore. A pile of numbers can't tell a
    //      designed queue from a jam, but a human can see one at a glance — this turns that glance into data. ----
    let inspecting = false;
    const onPick = (e: MouseEvent): void => {
      if (!inspecting || e.button !== 0) return;
      for (const f of this.factories) if (f.pickOreUnderPointer()) { e.stopPropagation(); return; } // a miss falls through to the camera
    };
    // PAUSE is what makes clicking an ore usable at all — a jam resolves itself long before it can be
    // described. [K] so it can be hit the instant something looks wrong. (B/E/F/P/R/X are taken; F is fly.)
    const pauseOut = phy.addReadout();
    const showPause = (): void => pauseOut(this.simPause ? "SIM PAUSED — camera still free · [`]" : "sim running · [`] to pause");
    phy.addToggles([
      { label: "pause sim  [`]", value: false, onChange: (v) => { this.simPause = v; showPause(); } },
    ]);
    phy.addButtons([
      { label: "step 1", onClick: () => { this.stepFrames += 1; } },
      { label: "step 10", onClick: () => { this.stepFrames += 10; } },
      { label: "step 60", onClick: () => { this.stepFrames += 60; } },
    ]);
    showPause();
    this.onSimPauseChanged = showPause; // the [K] shortcut flips the flag directly; keep the readout honest
    // Persisted: every source edit hot-reloads the page, which silently unticked this and made clicking look
    // broken. It survives a reload now, like the panel's own open/closed state.
    const INSPECT_KEY = "lfdev:oreInspector";
    const setInspecting = (v: boolean): void => {
      inspecting = v;
      localStorage.setItem(INSPECT_KEY, v ? "1" : "0");
      for (const f of this.factories) f.setInspect(v);
      // capture phase so the pick happens before orbit-drag bookkeeping; only bound while the mode is on
      if (v) this.scene.domElement.addEventListener("click", onPick, true);
      else this.scene.domElement.removeEventListener("click", onPick, true);
    };
    phy.addToggles([
      { label: "ore inspector (click an ore)", value: localStorage.getItem(INSPECT_KEY) === "1", onChange: setInspecting },
    ]);
    if (localStorage.getItem(INSPECT_KEY) === "1") setInspecting(true);

    // ---- Balance (live HP + damage-taken multipliers; lower dmg = tougher) ----
    const bal = dev.addBank("Balance", false);
    bal.addSlider({ label: "soldier hp", min: 20, max: 500, step: 10, value: balance.soldierHp, onChange: (v) => (balance.soldierHp = v) });
    bal.addSlider({ label: "giant hp", min: 100, max: 2000, step: 50, value: balance.giantHp, onChange: (v) => (balance.giantHp = v) });
    bal.addSlider({ label: "giant blast dmg×", min: 0.05, max: 1, step: 0.05, value: balance.giantBlastDmg, onChange: (v) => (balance.giantBlastDmg = v) });
    bal.addSlider({ label: "tank hp ×", min: 1, max: 12, step: 0.5, value: balance.tankHpMul, onChange: (v) => (balance.tankHpMul = v) });
    bal.addSlider({ label: "tank bullet dmg×", min: 0.05, max: 1, step: 0.05, value: balance.tankBulletDmg, onChange: (v) => (balance.tankBulletDmg = v) });
    bal.addSlider({ label: "tank blast dmg×", min: 0.05, max: 1, step: 0.05, value: balance.tankBlastDmg, onChange: (v) => (balance.tankBlastDmg = v) });

    // ---- Rarity ----
    const rar = dev.addBank("Rarity", false);
    rar.addSlider({ label: "tanks (max)", min: 0, max: 36, step: 1, value: balance.tankMax, onChange: (v) => (balance.tankMax = v) });
    rar.addSlider({ label: "giants/team (max)", min: 0, max: 12, step: 1, value: balance.giantMax, onChange: (v) => (balance.giantMax = v) });
    rar.addSlider({ label: "special troops", min: 0, max: 1, step: 0.05, value: balance.specialFrac, onChange: (v) => (balance.specialFrac = v) });

    // ---- Unit Points economy ----
    const eco = dev.addBank("Unit Points", false);
    eco.addSlider({ label: "UP regen /s", min: 0, max: 60, step: 1, value: balance.upRegen, onChange: (v) => (balance.upRegen = v) });
    const ecoOut = eco.addReadout();
    let eb = -1, er = -1;
    this.setEconomy = (b, r) => { const bb = Math.round(b), rr = Math.round(r); if (bb === eb && rr === er) return; eb = bb; er = rr; ecoOut(`UP  blue ${bb}   red ${rr}`); };

    // ---- Music ----
    const mus = dev.addBank("Music", false);
    mus.addSlider({ label: "volume", min: 0, max: 1, step: 0.05, value: music.getVolume(), onChange: (v) => music.setVolume(v) });
    mus.addButton("next track ⏭", () => music.next());

    // ---- Performance (live GPU/CPU cost — terrain tweaks move the GPU number even under vsync) ----
    const perf = dev.addBank("Performance");
    this.perfOut = perf.addReadout(true);

    // ---- Display / Resolution — dynamic-res scales the 3D buffer (never the UI) to hold 60fps ----
    const disp = dev.addBank("Display / Resolution");
    disp.addToggles([{ label: "auto-res → 60fps", value: this.drs, onChange: (v) => { this.drs = v; if (!v) this.scene.setRenderScale(this.manualScale); } }]);
    disp.addSlider({ label: "render scale (manual)", min: 0.4, max: 2.0, step: 0.05, value: 1, onChange: (v) => { this.manualScale = v; if (!this.drs) this.scene.setRenderScale(v); } });
    disp.addSlider({ label: "auto min scale", min: 0.3, max: 1.0, step: 0.05, value: 0.5, onChange: (v) => (this.drsMin = v) });
    disp.addSlider({ label: "auto max scale", min: 1.0, max: 2.5, step: 0.05, value: 2.0, onChange: (v) => (this.drsMax = v) });
    disp.addSlider({ label: "auto target GPU ms", min: 6, max: 16, step: 0.5, value: 13, onChange: (v) => (this.drsTargetMs = v) });

    // ---- Terrain look + LOD (campaign only — the terra material) ----
    if (this.campaign) {
      const set = (name: string) => (v: number): void => this.terraMesh?.handle.setUniform(name, v);
      const det = dev.addBank("Terrain — Detail");
      det.addSlider({ label: "max resolution", min: 0.5, max: 10, step: 0.05, value: 6, onChange: set("uMicroScale") });
      det.addSlider({ label: "grain strength", min: 0, max: 6, step: 0.02, value: 3, onChange: set("uGrainStrength") });
      det.addSlider({ label: "relief strength", min: 0, max: 4, step: 0.02, value: 0.1, onChange: set("uReliefStrength") });
      det.addSlider({ label: "base relief str", min: 0, max: 4, step: 0.02, value: 0.22, onChange: set("uBaseReliefStrength") });
      det.addSlider({ label: "mottling", min: 0, max: 6, step: 0.02, value: 3, onChange: set("uMottleStrength") });
      det.addSlider({ label: "cracks/veins", min: 0, max: 6, step: 0.02, value: 3, onChange: set("uCrackStrength") });
      det.addSlider({ label: "chasm darkness", min: 0, max: 2, step: 0.02, value: 0.85, onChange: set("uChasmDark") });
      det.addSlider({ label: "wall shading", min: 0, max: 2, step: 0.02, value: 1, onChange: set("uWallShade") });
      const dist = dev.addBank("Terrain — Distance / LOD", false);
      dist.addSlider({ label: "detail reach", min: 0.1, max: 4, step: 0.02, value: 0.26, onChange: set("uGrainReach") });
      dist.addSlider({ label: "base relief reach", min: 0.3, max: 4, step: 0.02, value: 2.2, onChange: set("uBaseReliefReach") });
      dist.addSlider({ label: "far smooth start px", min: 2, max: 80, step: 0.5, value: 33, onChange: set("uFarSmoothStart") });
      dist.addSlider({ label: "far smooth end px", min: 4, max: 140, step: 0.5, value: 73, onChange: set("uFarSmoothEnd") });
      dist.addSlider({ label: "far detail floor", min: 0, max: 1, step: 0.01, value: 0, onChange: set("uFarFloor") });
      // two LOD-viz modes (mutually exclusive): raw footprint bands, or the EFFECTIVE detail (which reacts
      // to the reach / far-smooth sliders so you can SEE their effect).
      let vizCbs: HTMLInputElement[] = [];
      const setViz = (mode: number, self: number): void => {
        this.debugLod = mode;
        this.terraMesh?.handle.setUniform("uDebugLod", mode);
        if (mode !== 0 && vizCbs[self ^ 1]) vizCbs[self ^ 1].checked = false; // uncheck the other
      };
      vizCbs = dist.addToggles([
        { label: "viz footprint (px)", value: false, onChange: (v) => setViz(v ? 1 : 0, 0) },
        { label: "viz effective detail", value: false, onChange: (v) => setViz(v ? 2 : 0, 1) },
      ]);

      // ---- Atmosphere — Fog: Roblox-Atmosphere-style aerial perspective (fades into the real sky). Slider
      // defaults are seeded from THIS world's biome fog so they match on open; tweaks persist + are copyable.
      const fp = getFogParams();
      const afog = dev.addBank("Atmosphere — Fog", false);
      afog.addSlider({ label: "density", min: 0, max: 5, step: 0.05, value: fp.density, onChange: (v) => (fogUniforms.uFogDensity.value = v * FOG_DENSITY_UNIT) });
      afog.addSlider({ label: "air thickness (studs)", min: 300, max: 5000, step: 50, value: fp.airThickness, onChange: (v) => (fogUniforms.uFogHeightFalloff.value = 1 / Math.max(50, v)) });
      afog.addSlider({ label: "sun glow", min: 0, max: 2, step: 0.02, value: fp.glare, onChange: (v) => (fogUniforms.uFogGlare.value = v) });
      afog.addSlider({ label: "sun glow focus", min: 0, max: 0.9, step: 0.02, value: fp.glareG, onChange: (v) => (fogUniforms.uFogGlareG.value = v) });
      afog.addSlider({ label: "max opacity", min: 0.3, max: 1, step: 0.02, value: fp.max, onChange: (v) => (fogUniforms.uFogMax.value = v) });
      afog.addSlider({ label: "ground level (studs)", min: -400, max: 1200, step: 20, value: fp.heightRef, onChange: (v) => (fogUniforms.uFogHeightRef.value = v) });

      // ---- Water (garden battles only; no-op elsewhere). Slider defaults match water.ts uniform defaults. ----
      const setW = (name: string) => (v: number): void => this.terraMesh?.water?.setUniform(name, v);
      const wat = dev.addBank("Water", false);
      wat.addSlider({ label: "reflection", min: 0, max: 1, step: 0.02, value: 0.5, onChange: setW("uReflStrength") });
      wat.addSlider({ label: "reflection distort", min: 0, max: 0.25, step: 0.005, value: 0.15, onChange: setW("uDistort") });
      wat.addSlider({ label: "fresnel power", min: 1, max: 8, step: 0.1, value: 1.7, onChange: setW("uFresnelPow") });
      wat.addSlider({ label: "ripple strength", min: 0, max: 20, step: 0.2, value: 1, onChange: setW("uRippleAmp") });
      wat.addSlider({ label: "ripple scale", min: 0.004, max: 0.04, step: 0.001, value: 0.012, onChange: setW("uRippleScale") });
      wat.addSlider({ label: "ripple speed", min: 0, max: 3, step: 0.05, value: 0.45, onChange: setW("uRippleSpeed") });
      wat.addSlider({ label: "depth tint", min: 0, max: 0.1, step: 0.002, value: 0.042, onChange: setW("uDepthTint") });
      wat.addSlider({ label: "shallow alpha", min: 0.2, max: 1, step: 0.02, value: 0.88, onChange: setW("uAlphaShallow") });
      wat.addSlider({ label: "deep alpha", min: 0.3, max: 1, step: 0.02, value: 0.6, onChange: setW("uAlphaDeep") });
      wat.addSlider({ label: "shore detail", min: 0, max: 2.5, step: 0.02, value: 1.5, onChange: setW("uShoreFoam") });
      wat.addSlider({ label: "shore width", min: 5, max: 200, step: 2, value: 9, onChange: setW("uShoreWidth") });

      // ---- Grass (garden battles only; no-op elsewhere). Slider defaults match grass.ts uniform defaults. ----
      const setG = (name: string) => (v: number): void => this.terraMesh?.grass?.setUniform(name, v);
      const gr = dev.addBank("Grass", false);
      gr.addSlider({ label: "blade height", min: 2, max: 60, step: 1, value: 16, onChange: setG("uBlade") });
      gr.addSlider({ label: "blade width", min: 0.5, max: 8, step: 0.1, value: 2.4, onChange: setG("uBladeW") });
      gr.addSlider({ label: "wind strength", min: 0, max: 12, step: 0.2, value: 3, onChange: setG("uWindStr") });
      gr.addSlider({ label: "brightness", min: 0.5, max: 1.6, step: 0.02, value: 1.18, onChange: setG("uBright") });
      gr.addSlider({ label: "sun shading", min: 0, max: 0.6, step: 0.02, value: 0.22, onChange: setG("uSunStr") });
      gr.addSlider({ label: "ground mottle", min: 0, max: 5, step: 0.1, value: 3, onChange: setG("uMottle") });

      // ---- Clouds (all biomes). Push each uniform to the sky dome AND the terra material: coverage/scale/
      // churn are SHARED (keeps ground shadows tracking the sky); density/steps/puff/dither are sky-only and
      // no-op harmlessly on the terra handle. Sliders seed from THIS biome's preset so they start neutral. ----
      const cp = this.cloud;
      const setC = (name: string) => (v: number): void => {
        this.atmosphere?.setCloudUniform(name, v);
        this.terraMesh?.handle.setUniform(name, v);
      };
      const cl = dev.addBank("Clouds", false);
      cl.addSlider({ label: "coverage", min: 0, max: 1, step: 0.01, value: cp?.coverage ?? 0.44, onChange: setC("uCloudCoverage") });
      cl.addSlider({ label: "density", min: 0, max: 4, step: 0.05, value: cp?.density ?? 1.9, onChange: setC("uCloudDensity") });
      cl.addSlider({ label: "cloud size", min: 1000, max: 8000, step: 100, value: cp?.scale ?? 3100, onChange: setC("uCloudScale") });
      cl.addSlider({ label: "puffiness", min: 0.2, max: 2.5, step: 0.05, value: 1, onChange: setC("uPuff") });
      cl.addSlider({ label: "softness", min: 0, max: 1, step: 0.02, value: 0.5, onChange: setC("uSoft") });
      cl.addSlider({ label: "detail steps", min: 4, max: 16, step: 1, value: cp?.steps ?? 12, onChange: setC("uCloudSteps") });
      cl.addSlider({ label: "grain (dither)", min: 0, max: 1, step: 0.05, value: 1, onChange: setC("uDither") });
      cl.addSlider({ label: "churn rate", min: 0, max: 4, step: 0.05, value: 1, onChange: setC("uEvolve") });
      cl.addSlider({ label: "drift speed", min: 0, max: 4, step: 0.05, value: 1, onChange: (v) => (this.cloudDriftMul = v) });
    }

    // ---- Stats readout ---- (build runs AFTER the first regenerate, so render the cached stats now)
    // ── SHADERS — every dial on the post pass, plus per-effect on/off. Toggling REBUILDS the shader with the
    //    dead effects compiled out (see grade.ts), so switching one off is a genuine saving, not a branch.
    //    "copy defaults" dumps the current state as a paste-able GRADE_DEFAULTS block: tune by eye here, then
    //    hand the numbers straight back into the source rather than transcribing eight sliders.
    const shd = dev.addBank("Shaders", false);
    const G = this.scene.grade;
    const shdOut = shd.addReadout();
    const showShd = (): void => {
      const o = G.opts;
      const on = [o.sharpen > 0 && "sharpen", o.lut && "lut", o.crt && "crt", o.outline > 0 && "outline",
                  o.vignette > 0 && "vig", o.chroma > 0 && "chroma", o.grain > 0 && "grain"].filter(Boolean);
      shdOut(`post ${G.enabled ? "ON" : "OFF"} · ${on.length ? on.join(" · ") : "all effects off"}`);
    };
    shd.addToggles([
      { label: "post pass", value: G.enabled, onChange: (v) => { G.enabled = v; showShd(); } },
      { label: "CRT", value: G.opts.crt, onChange: (v) => { G.set({ crt: v }); showShd(); } },
      { label: "LUT grade", value: G.opts.lut, onChange: (v) => { G.set({ lut: v }); showShd(); } },
    ]);
    shd.addButtons([
      { label: "neutral", onClick: () => G.setLut(LUTS.neutral()) },
      { label: "steel", onClick: () => G.setLut(LUTS.steel()) },
      { label: "ember", onClick: () => G.setLut(LUTS.ember()) },
      { label: "probe", onClick: () => G.setLut(LUTS.probe()) },
    ]);
    shd.addSlider({ label: "sharpen (CAS)", min: 0, max: 1.5, step: 0.01, value: GRADE_DEFAULTS.sharpen,
                    onChange: (v) => { G.set({ sharpen: v }); showShd(); } });
    shd.addSlider({ label: "depth outline", min: 0, max: 1, step: 0.01, value: GRADE_DEFAULTS.outline,
                    onChange: (v) => { G.set({ outline: v }); showShd(); } });
    shd.addSlider({ label: "CRT amount", min: 0, max: 1, step: 0.01, value: 1,
                    onChange: (v) => { G.setCrtAmount(v); } });
    shd.addSlider({ label: "CRT mask pitch", min: 1, max: 6, step: 0.1, value: 3,
                    onChange: (v) => { G.setMaskPitch(v); } });
    shd.addSlider({ label: "CRT curvature", min: 0, max: 0.6, step: 0.005, value: 0.18,
                    onChange: (v) => { G.setCurvature(v); } });
    shd.addSlider({ label: "vignette", min: 0, max: 1, step: 0.01, value: GRADE_DEFAULTS.vignette,
                    onChange: (v) => { G.set({ vignette: v }); showShd(); } });
    shd.addSlider({ label: "chromatic ab.", min: 0, max: 0.02, step: 0.0002, value: GRADE_DEFAULTS.chroma,
                    onChange: (v) => { G.set({ chroma: v }); showShd(); } });
    shd.addSlider({ label: "film grain", min: 0, max: 0.2, step: 0.002, value: GRADE_DEFAULTS.grain,
                    onChange: (v) => { G.set({ grain: v }); showShd(); } });
    shd.addButtons([
      { label: "copy defaults", accent: "#8cff7a", onClick: () => {
        const txt = G.snapshotDefaults();
        void navigator.clipboard.writeText(txt).then(() => shdOut("⎘ copied — paste into grade.ts"),
                                                     () => shdOut("clipboard blocked — see console"));
        console.log(txt);   // always log too, so the values are recoverable if the clipboard is refused
      } },
    ]);
    showShd();

    const stats = dev.addBank("Stats", false);
    this.statsOut = stats.addReadout(true);
    if (this.lastStats) { const f = formatStats(...this.lastStats); this.statsOut(f.text, f.color); }

    this.dev = dev;
  }

  /** Level stats/validation readout — caches the args so the first (pre-panel-build) call still shows once
   *  the Stats bank exists, and refreshes it on every subsequent regenerate. */
  private showStats(...args: Parameters<typeof formatStats>): void {
    this.lastStats = args;
    if (this.statsOut) { const f = formatStats(...args); this.statsOut(f.text, f.color); }
  }

  private lastLevelKey = "";

  private regenerate(s: PanelState): void {
    if (this.group) {
      this.scene.scene.remove(this.group);
      disposeGroup(this.group);
      this.group = null;
    }
    // fanfare only when the actual level changes (not on every curbs/grid/toggle re-emit)
    const key = `${s.seed}#${s.level}`;
    const levelChanged = key !== this.lastLevelKey;
    if (this.lastLevelKey && levelChanged) sfx.play("round");
    this.lastLevelKey = key;

    // ---- TERRA branch: campaign battles fight on the open sculpted battlefield ----
    if (this.campaign && this.biome) {
      const t = generateTerra(s.seed, s.level, this.biome.key);
      this.terraLevel = t;
      const lvlLike = t as unknown as GeneratedLevel;
      const bnav = this.battleNav(t.nav); // wall-aware walkability: units/tanks/player can't cross or shoot through factory walls
      this.army.setLevel(lvlLike, bnav);
      this.army.setVisible(s.armies);
      this.tanks.setLevel(lvlLike, bnav);
      this.tanks.setVisible(s.tanks);
      for (const b of this.banks) b.reset();
      // projectiles fly/bounce against the SHOT surface (flat hazards never deflect bullets);
      // climbMax 20/substep: rolling ground passes, crevasse/mesa faces bounce
      const shotNav: NavGraph = { ...t.nav, heightAt: (x, z) => t.nav.shotHeightAt!(x, z) };
      this.projectiles.setNav(shotNav, 20);
      this.projectiles.setVisible(s.armies || s.tanks);
      this.healthBars.setVisible(s.armies || s.tanks);
      this.hazards.setField(t.field);
      this.hazards.setSides([this.army, this.tanks]);

      this.nav = bnav; // the player walks the walkability surface (hazard-aware + wall-aware)
      this.bbox = t.stats.bbox;
      const { walls, spawn, footprint, floorY } = this.playerCombat.onLevel(this.nav);
      this.shopWalls = walls;
      this.shopFootprint = footprint;
      this.shopFloorY = floorY;
      this.playerStart = spawn.clone();
      if (this.playing && levelChanged) this.respawnPlayer();
      // Hand each factory ITS OWN pad rect so the build grid is concentric with the cement the terrain
      // stamped (team 0 starts, team 1 holds the core).
      { const cl = this.climateForBattle();
        for (const f of this.factories) f.onLevel(t.nav, cl.sev, cl.kind, f.team === 0 ? t.startRect : t.coreRect); }

      const tm = buildTerraMesh(t, this.atmosphere?.getSunDir());
      this.terraMesh = tm;
      this.scene.registerCostProbe("terrain", tm.mesh); // (re-)point the terrain GPU-cost probe at the new mesh
      this.scene.registerCostProbe("skirt", tm.skirt);
      if (tm.grass) this.scene.registerCostProbe("grass", tm.grass.mesh);
      // skinned units + TANKS + grass: skip in the bloom-occluder + reflection re-renders. Tanks were the one
      // dynamic group missing here, and they are the expensive one: 30 tanks are ~100 individual meshes (72 tris
      // each), so they were costing ~0.95ms in the occluder and ~0.45ms in the reflection to contribute a
      // silhouette nobody sees — infantry and giants have never occluded or reflected either. Measured ~1.4ms.
      this.scene.heavyExclude = [...this.army.renderGroups, ...this.tanks.renderGroups, ...(tm.grass ? [tm.grass.mesh] : [])];
      // The FACTORY is the largest draw-call block in the scene (measured: 337 calls a pass on a late-game base,
      // submitted 4x a frame). It has to stay in the bloom occluder — its buildings are what hide the ore glow —
      // but a quarter-res, ripple-distorted reflection can lose it for a quarter of the cost back.
      this.scene.reflectExclude = this.factories.map((fx) => fx.renderRoot);
      if (tm.water) { // water plane owns onBefore/onAfterRender (reflection + occlusion query) — COMPOSE, don't clobber
        const wm = tm.water.mesh, reflectCb = wm.onBeforeRender, occEnd = wm.onAfterRender; // capture water's own hooks first
        this.scene.registerCostProbe("water", wm);
        const beginCb = wm.onBeforeRender, timerEnd = wm.onAfterRender; // the "water" cost-probe begin/end
        wm.onBeforeRender = (r, s, c, g, m, grp): void => {
          this.scene.reflectionBegin();
          const ex = [...this.scene.heavyExclude, ...this.scene.reflectExclude], vis = ex.map((o) => o.visible);
          for (const o of ex) o.visible = false;      // the reflection needn't re-skin the units / re-draw grass
          reflectCb.call(wm, r, s, c, g, m, grp);       // occlusion read + reflect() + occlusion begin
          for (let i = 0; i < ex.length; i++) ex[i].visible = vis[i];
          this.scene.reflectionEnd();
          beginCb.call(wm, r, s, c, g, m, grp);         // then the "water" probe brackets the plane's own fragment draw
        };
        wm.onAfterRender = (r, s, c, g, m, grp): void => { occEnd.call(wm, r, s, c, g, m, grp); timerEnd.call(wm, r, s, c, g, m, grp); }; // occlusion end + water-timer end
      } else { this.scene.costs.water = 0; this.scene.costs.reflection = 0; } // no water this battle → both honest 0
      this.dev?.applyAll(); // re-push the DEV panel's current values onto the freshly-built material
      tm.handle.setUniform("uDebugLod", this.debugLod); // keep the LOD-viz mode across regenerates
      this.group = tm.group;
      this.scene.scene.add(this.group);
      this.scene.setGridVisible(false);

      // frame the mid-field, not the whole 13k-stud world — the full span would orbit the camera
      // out beyond the biome fog (the minimap carries the overview)
      const fc = { x: (t.start.x + t.exit.x) / 2, z: (t.start.z + t.exit.z) / 2 };
      const fr = Math.min(3400, t.padGap * 0.55);
      this.frameRect = { minX: fc.x - fr, maxX: fc.x + fr, minZ: fc.z - fr, maxZ: fc.z + fr };
      this.rig.frameLevel(this.frameRect);
      this.minimap.setLevel(lvlLike); // pseudo-tiles: elevation + hazard map
      this.showStats(t.stats, {
        ok: true, tiles: t.stats.tiles, cells: t.stats.tiles, overlaps: 0, rampsWellFormed: true,
        solvable: true, fullyTraversable: true, reachableCells: t.stats.tiles,
        curbBlockedTransitions: 0, curbBlockedExamples: [], hasRoom: true, hasRamp: true, hasTurn: true,
        errors: t.carved ? ["terra: carve fallback used"] : [],
      }, t.plan, true);
      // eslint-disable-next-line no-console
      console.log(`[longfight] TERRA level ${t.level} seed "${t.seedWord}" (${t.seedInt}): ` +
        `${t.stats.tiles} cells, passes=${t.stats.arenas}, carved=${t.carved}, gap=${t.padGap}`);
      if (levelChanged) this.buildEnvironment();
      patchSceneFog(this.scene.scene); // bind the shared aerial-fog uniforms onto every fresh world material
      return;
    }

    const lvl = generateLevel(s.seed, s.level);
    const report = validateLevel(lvl);
    this.army.setLevel(lvl);
    this.army.setVisible(s.armies);
    this.tanks.setLevel(lvl);
    this.tanks.setVisible(s.tanks);
    for (const b of this.banks) b.reset(); // fresh UP economy for the new battle
    if (this.tanks.navGraph) this.projectiles.setNav(this.tanks.navGraph); // share the tanks' nav
    this.projectiles.setVisible(s.armies || s.tanks);
    this.healthBars.setVisible(s.armies || s.tanks);

    this.nav = this.tanks.navGraph; // the player walks this same nav
    this.bbox = lvl.stats.bbox;
    if (this.nav) {
      const { walls, spawn, footprint, floorY } = this.playerCombat.onLevel(this.nav); // (re)build the shop
      this.shopWalls = walls;
      this.shopFootprint = footprint;
      this.shopFloorY = floorY;
      this.playerStart = spawn.clone();
    }
    if (this.playing && levelChanged) this.respawnPlayer(); // new map underfoot → drop onto its start pad
    if (this.nav) { const cl = this.climateForBattle(); for (const f of this.factories) f.onLevel(this.nav, cl.sev, cl.kind); } // (re)site both factories

    this.group = buildLevelMesh(lvl, { curbs: s.curbs });
    this.scene.scene.add(this.group);
    this.scene.setGridVisible(s.grid);

    this.rig.frameLevel(lvl.stats.bbox);
    this.minimap.setLevel(lvl);
    this.showStats(lvl.stats, report, lvl.plan, lvl.reachedTarget);

    // eslint-disable-next-line no-console
    console.log(
      `[longfight] level ${lvl.level} seed "${lvl.seedWord}" (${lvl.seedInt}): ` +
        `${lvl.stats.tiles} tiles, valid=${report.ok}`,
      report.errors,
    );

    if (this.campaign && levelChanged) this.buildEnvironment();
    patchSceneFog(this.scene.scene);
  }

  /** Campaign only: the surrounding planet — terrain the arena sits on, a weak energy perimeter marking
   *  the play boundary, and a few non-playable generator "sprawl" chunks so the base extends to the fog. */
  private buildEnvironment(): void {
    if (!this.biome || !this.campaign) return;
    const S = this.scene.scene;
    if (this.terrain) { S.remove(this.terrain.mesh); this.terrain.dispose(); }
    this.terrain = null;
    if (this.perimeter) { S.remove(this.perimeter.group); this.perimeter.dispose(); }
    for (const g of this.sprawl) { S.remove(g); disposeGroup(g); }
    this.sprawl = [];

    const b = this.bbox;
    if (this.terraLevel) {
      // TERRA: the battlefield IS the planet surface (its own skirt runs to the fog) — no decorative
      // under-terrain, no generator sprawl (built chunks read wrong scattered on wild ground).
      // The energy perimeter still marks the play boundary.
      this.perimeter = new Perimeter(b, PERIMETER_COLOR);
      S.add(this.perimeter.group);
      return;
    }
    this.terrain = buildTerrain(b, this.biome);
    S.add(this.terrain.mesh);
    this.perimeter = new Perimeter(b, PERIMETER_COLOR);
    S.add(this.perimeter.group);

    // decorative generator-sprawl: small non-validated level chunks planted on the terrain around the arena
    const cx = (b.minX + b.maxX) / 2, cz = (b.minZ + b.maxZ) / 2;
    const arenaR = 0.5 * Math.hypot(b.maxX - b.minX, b.maxZ - b.minZ);
    const N = 4;
    for (let k = 0; k < N; k++) {
      const ang = (k / N) * Math.PI * 2 + 0.6;
      const rad = arenaR * 1.9 + 1800;
      const px = cx + Math.cos(ang) * rad, pz = cz + Math.sin(ang) * rad;
      const lvl = generateLevel(`sprawl-${this.campaign.seed}-${k}`, 1 + (k % 2));
      const g = buildLevelMesh(lvl, { curbs: false, decorative: true });
      const bc = lvl.stats.bbox;
      g.position.set(px - (bc.minX + bc.maxX) / 2, this.terrain.heightAt(px, pz) - topY(0), pz - (bc.minZ + bc.maxZ) / 2);
      g.rotation.y = ang;
      S.add(g);
      this.sprawl.push(g);
    }
  }

  freeze = false; // dev: pause the rig so the camera can be positioned manually
  // dev: pause the SIM (ore, factories, units) while leaving the camera free, so a jam can be looked at,
  // clicked and described before it resolves itself. Deliberately separate from `freeze` — inspecting a
  // frozen scene is exactly when you most need to fly around it. `stepFrames` nudges time forward by hand.
  simPause = false;
  stepFrames = 0;
  private onSimPauseChanged: (() => void) | null = null;

  // ---- first-person play mode -------------------------------------------------
  private enterPlay(): void {
    if (!this.nav || this.playing) return;
    this.player.softRespawn(this.playerSpawn(), this.spawnYaw()); // heal + reposition, keep upgrades
    this.input.requestFly(); // pointer lock (same path the fly camera uses)
    this.playing = true;
    this.awaitingLock = true;
    this.playEnterAt = performance.now();
    this.playerCombat.enter();
    this.minimap.setCompact(true); // move the map top-right, clear of the gun + bottom HUD
    this.playerHud.show();
    this.playerHud.setHealth(1);
  }

  private exitPlay(): void {
    this.playing = false;
    this.awaitingLock = false;
    this.playerCombat.exit();
    this.minimap.setCompact(false);
    this.playerHud.hide();
    this.scene.camera.fov = CAMERA.fov; // restore the narrower spectator FOV
    this.scene.camera.updateProjectionMatrix();
    this.input.exitFly();
    // hand back to the spectator — on terra reuse the mid-field frame (the full bbox would park
    // the camera out in the fog; review finding)
    this.rig.frameLevel(this.frameRect ?? this.bbox);
  }

  private updatePlayer(dt: number): void {
    if (!this.input.locked) {
      // No pointer lock this frame. Either we just released it (Esc → back to spectator), or we're
      // still acquiring it right after entering. If the request was DENIED (Chrome throttles
      // re-locking for ~1s after an Esc-unlock) or it simply never arrives, bail to spectator so
      // play mode can never deadlock — pressing P again once the cooldown passes re-enters cleanly.
      if (!this.awaitingLock) return this.exitPlay(); // was locked, now released → leave
      if (this.input.lockError || performance.now() - this.playEnterAt > 1000) return this.exitPlay();
      return; // still acquiring the lock this frame
    }
    this.awaitingLock = false;
    this.playerPrevX = this.player.position.x;
    this.playerPrevZ = this.player.position.z;
    this.player.update(dt, this.input);
    this.collidePlayer();
    if (this.playerCombat.update(dt)) this.deathRespawn(); // fire/shop/HUD; true = player went down
  }

  /** Revive at the shop spawn WITHOUT wiping upgrades (points penalty handled by PlayerCombat). */
  private deathRespawn(): void {
    this.player.softRespawn(this.playerSpawn(), this.spawnYaw());
    if (this.campaign && !this.campaignResolved) {
      this.campaignDeaths++;
      this.updateCampaignBanner();
      if (this.campaignDeaths >= CAMPAIGN_MAX_DEATHS) this.campaignFinish(false); // op failed
    }
  }

  /** Wrap a level nav so the shared walkability surface also respects factory WALLS: any world point inside a
   *  floor-0 wall strip reads as non-walkable, so army units, tanks, and the player can't cross it and won't hold
   *  targeting line-of-sight through it. Purely additive — a passthrough whenever no walls exist, so battles with
   *  no walls behave exactly as before. Reads the factories LIVE, so walls placed mid-battle take effect at once. */
  private battleNav(raw: NavGraph): NavGraph {
    const facs = this.factories;
    const blocked = (x: number, z: number): boolean => { for (const f of facs) if (f.blocksAt(x, z)) return true; return false; };
    return { ...raw, heightAt: (x, z) => (blocked(x, z) ? null : raw.heightAt(x, z)) };
  }

  /** Ground-follow + collide-and-slide against the level nav (mirrors the army's slideMove). XZ is
   *  kept on walkable ground; a step up larger than PLAYER.stepUp is a wall (blocked); stepping down
   *  just leaves you airborne for gravity to resolve. */
  private collidePlayer(): void {
    const nav = this.nav;
    if (!nav) return;
    const p = this.player.position;
    const eye = PLAYER.eyeHeight;
    const feetY = p.y - eye;
    const pr = PLAYER.radius;
    const inWall = (x: number, z: number): boolean => {
      for (const w of this.shopWalls) {
        if (x > w.x0 - pr && x < w.x1 + pr && z > w.z0 - pr && z < w.z1 + pr) return true;
      }
      return false;
    };
    // ground height: the shop floor (its own slab, off the NPC nav) inside its footprint, else the nav
    const groundAt = (x: number, z: number): number | null => {
      const f = this.shopFootprint;
      if (f && x >= f.x0 && x <= f.x1 && z >= f.z0 && z <= f.z1) return this.shopFloorY;
      return nav.heightAt(x, z);
    };
    const okAt = (x: number, z: number): boolean => {
      if (inWall(x, z)) return false; // blocked by a shop-building wall
      const g = groundAt(x, z);
      return g != null && g - feetY <= PLAYER.stepUp; // on the map/shop floor AND not a wall to climb
    };
    // reuse this ground sample below instead of re-sampling groundAt(p.x,p.z) a second time in the common
    // (no-slide) frame; only re-sample when a slide/wedge actually moved p.
    const g0 = groundAt(p.x, p.z);
    let ground = g0;
    if (!(!inWall(p.x, p.z) && g0 != null && g0 - feetY <= PLAYER.stepUp)) {
      if (okAt(p.x, this.playerPrevZ)) p.z = this.playerPrevZ; // slide along X
      else if (okAt(this.playerPrevX, p.z)) p.x = this.playerPrevX; // slide along Z
      else {
        p.x = this.playerPrevX; // wedged: hold the last good spot
        p.z = this.playerPrevZ;
      }
      ground = groundAt(p.x, p.z);
    }
    if (ground == null) return this.player.applyGround(true); // safety (post-slide should be on map)
    const targetY = ground + eye;
    if (p.y <= targetY) {
      p.y = targetY;
      this.player.applyGround(true);
    } else {
      this.player.applyGround(false);
    }
  }

  private respawnPlayer(): void {
    this.player.softRespawn(this.playerSpawn(), this.spawnYaw());
  }

  /** Spawn just outside the shop door (feet height + eye), falling back to the start pad / origin. */
  private playerSpawn(): THREE.Vector3 {
    if (this.playerStart) {
      return new THREE.Vector3(this.playerStart.x, this.playerStart.y + PLAYER.eyeHeight, this.playerStart.z);
    }
    const nav = this.nav;
    const k = nav ? nav.startKeys[0] ?? nav.coreKeys[0] : undefined;
    const pt = k && nav ? nav.point.get(k) : null;
    if (!pt) return new THREE.Vector3(0, topY(0) + PLAYER.eyeHeight, 0);
    return new THREE.Vector3(pt.x, pt.y + PLAYER.eyeHeight, pt.z);
  }

  /** Face from the start pad toward the enemy core (Player forward = (-sin yaw, -cos yaw)). */
  private spawnYaw(): number {
    const nav = this.nav;
    if (!nav) return 0;
    const s = nav.point.get(nav.startKeys[0] ?? "");
    const c = nav.point.get(nav.coreKeys[0] ?? "");
    if (!s || !c) return 0;
    return Math.atan2(-(c.x - s.x), -(c.z - s.z));
  }

  /** The factory climate requirement for this battle's biome: hot/cold worlds need power + cooling. */
  private climateForBattle(): { sev: number; kind: "none" | "hot" | "cold" } {
    const z = this.campaign?.zone;
    if (z === "hot") return { sev: 1, kind: "hot" };
    if (z === "cold") return { sev: 1, kind: "cold" };
    return { sev: 0, kind: "none" };
  }

  private frame = (): void => {
    const t = performance.now();
    const nowSec = t / 1000;
    let dt = (t - this.lastTime) / 1000;
    this.lastTime = t;
    if (dt > 1 / 20) dt = 1 / 20;

    // P drops you into the battle as a first-person little-person; Esc leaves.
    if (this.input.consumeKeyPress("KeyP") && !this.playing) this.enterPlay();
    // ` freezes the sim where it stands, so whatever just went wrong can be looked at and clicked. Backquote
    // deliberately: every building hotkey is a letter or digit, so a debug key there can never collide with one
    // (this was on KeyK, which was then the Combustor — in build mode it both placed and paused. The
    //  Combustor has since been archived and KeyK is free again, but keep this on Backquote: the rule is
    //  what stops the next building hotkey from re-creating the collision.)
    if (this.input.consumeKeyPress("Backquote")) { this.simPause = !this.simPause; this.onSimPauseChanged?.(); }
    // play mode is NOT gated by the dev `freeze` flag, so Esc can always exit it; freeze only
    // pauses the spectator rig (its original purpose — manual camera positioning).
    if (this.playing) this.updatePlayer(dt);
    else if (!this.freeze) this.rig.update(dt);
    this.checkCampaignObjective(); // campaign op: win on reaching the enemy core
    const focus = this._focus;
    if (this.playing) { focus.x = this.player.position.x; focus.z = this.player.position.z; }
    else this.rig.focusPoint(focus);
    // the sound listener follows the camera focus, so the battle you're looking at is the loudest
    // (focus is a ground XZ point; y barely matters against the huge XZ distances)
    sfx.setListener(focus.x, 0, focus.z);
    // sky dome follows the EYE (never approachable/escapable); wind VFX still wrap the focus.
    this.atmosphere?.update(dt, this.scene.camera.position, focus.x, focus.z);
    this.atmosphere?.setViewProj(this.scene.camera); // for the cloud-depth write (occlude terrain from above)
    // shared cloud drift (world-XZ studs) — same offset to the sky raymarch and the ground shadows so
    // the shadows track the clouds overhead. Negative ⇒ clouds move ALONG the wind vector.
    let cloudSx = 0, cloudSz = 0;
    if (this.cloud) {
      this.cloudPhase += dt * this.cloudDriftMul; // integrate ⇒ changing the speed slider never teleports the deck
      cloudSx = -this.cloud.windX * this.cloudPhase;
      cloudSz = -this.cloud.windZ * this.cloudPhase;
      this.atmosphere?.setCloudScroll(cloudSx, cloudSz);
    }
    // ---- TERRA per-frame: biome hazards + living-surface shader + rocky-world dust storms ----
    if (this.terraLevel) {
      this.hazards.update(dt);
      if (this.playing) this.hazards.playerTick(this.player, dt);
      const tSec = nowSec;
      this.terraMesh?.handle.setTime(tSec);
      this.terraMesh?.handle.setCloudWind(cloudSx, cloudSz);
      this.terraMesh?.water?.setFrame(tSec, this.scene.renderer.toneMappingExposure, this.scene.scene.fog);
      const gcam = this.scene.camera.position;
      this.terraMesh?.grass?.setFrame(focus.x, focus.z, gcam.x, gcam.y, gcam.z, tSec, this.cloud?.windX ?? 1, this.cloud?.windZ ?? 0.3);
      this.terraMesh?.grass?.setCloudWind(cloudSx, cloudSz); // same drift as terrain ⇒ grass cloud shadow tracks ground
      if (this.biome?.key === "temperate") {
        // dust storms: slow waves of visibility loss rolling over the rust plain — pulse the aerial-fog
        // density (was the old THREE.Fog.far); the haze thickens without changing color or the curve.
        const storm = Math.max(0, Math.sin(tSec * 0.03) * 0.6 + Math.sin(tSec * 0.011 + 2.1) * 0.5 - 0.35);
        setFogStorm(1 + 1.6 * Math.min(1, storm));
      }
    }
    this.perimeter?.update(nowSec); // energy-curtain pulse
    for (const b of this.banks) b.tick(dt); // regenerate each team's Unit Points
    this.setEconomy(this.banks[0].up, this.banks[1].up);
    this.healthBars.reset();
    // DEV pause: `sdt` is the dt handed to everything that SIMULATES. The camera, HUD, tile readout and ore
    // inspector keep running off the real dt, so a frozen jam can be flown around, clicked and described.
    let sdt = dt;
    if (this.simPause) { sdt = this.stepFrames > 0 ? 1 / 60 : 0; if (this.stepFrames > 0) this.stepFrames--; }
    this.army.update(sdt);
    this.tanks.update(sdt);
    this.projectiles.update(sdt);
    this.healthBars.render(this.scene.camera);
    this.scene.focusSun(focus.x, focus.z);
    this.minimap.draw(focus);
    // fog-patch sweep EVERY frame (idempotent, ~300 objects ≈ free): the army/tanks create fresh
    // materials when waves spawn — mid-battle, AFTER regenerate's sweep. An unpatched material used to
    // compile the aerial fog chunk with unbound uniforms → its samplerCube collided with the shadow
    // sampler on texture unit 0 → GL dropped the draws → INVISIBLE UNITS (+ their orphaned shadows
    // roaming the ground). Now unpatched materials compile safe classic fog, and this sweep upgrades
    // them to aerial within a frame of creation.
    patchSceneFog(this.scene.scene);
    for (const f of this.factories) f.update(sdt, !this.playing); // both factories: ore-flow + assembly sim; player build input only from the free camera
    // dynamic resolution: adjust the render scale BEFORE rendering, so we immediately re-fill the resized
    // buffer this same frame. (Resizing AFTER render() clears the just-drawn buffer to black, which then
    // gets composited → the black flicker seen on slower devices.) Nudge off measured GPU ms, throttled +
    // quantized so we don't thrash/pop; drop res when over budget (hold 60fps), climb toward supersampling.
    if (this.drs && t - this.drsLast > 350) {
      const gpu = this.scene.costs.total;
      if (gpu > 0) {
        let ns = this.scene.renderScale;
        if (gpu > this.drsTargetMs) ns -= 0.08;
        else if (gpu < this.drsTargetMs - 3.5) ns += 0.05;
        ns = Math.max(this.drsMin, Math.min(this.drsMax, Math.round(ns * 20) / 20));
        if (Math.abs(ns - this.scene.renderScale) > 0.001) this.scene.setRenderScale(ns);
      }
      this.drsLast = t;
    }
    this.scene.render();
    if (this.playing) this.playerCombat.render(this.scene.renderer); // gun viewmodel on top
    this.input.endFrame();
    // live performance readout (throttled ~3/s): real GPU ms (moves as you tweak terrain), CPU ms, fps
    if (this.perfOut) {
      this.perfFrames++; this.perfDt += dt; this.perfCpu += performance.now() - t;
      if (t - this.perfLast > 350) {
        const fps = this.perfDt > 0 ? this.perfFrames / this.perfDt : 0;
        const cpu = this.perfCpu / Math.max(1, this.perfFrames);
        const c = this.scene.costs;
        const total = c.total ?? -1, terrain = c.terrain ?? -1, skirt = c.skirt ?? -1, sky = c.sky ?? -1, grass = c.grass ?? -1;
        const water = c.water ?? -1, shadow = c.shadow ?? -1, bloom = c.bloom ?? -1, ore = c.ore ?? -1;
        const factory = c.factory ?? -1, units = c.units ?? -1, reflection = c.reflection ?? -1;
        // "rest" = only the still-un-channeled bits: TANKS, projectiles, health bars, campaign decor, the
        //   transparent-glass tail. (total includes bloom; factory/units/reflection are the heavy carve-outs.)
        const known = [terrain, skirt, sky].every((v) => v >= 0);
        const sub = [grass, water, shadow, bloom, ore, factory, units, reflection]
          .reduce((a, v) => a + (v >= 0 ? v : 0), terrain + skirt + sky);
        const rest = total >= 0 && known ? Math.max(0, total - sub) : -1;
        const fmt = (v: number): string => (v >= 0 ? v.toFixed(2) : "  – ");
        const rs = this.scene.renderScale;
        const bw = Math.round(window.innerWidth * rs), bh = Math.round(window.innerHeight * rs);
        this.perfOut(
          `GPU by sector (ms):\n` +
          `  terrain ${fmt(terrain)}\n  grass   ${fmt(grass)}\n  skirt   ${fmt(skirt)}\n  sky     ${fmt(sky)}\n  water   ${fmt(water)}\n` +
          `  shadow  ${fmt(shadow)}\n  factory ${fmt(factory)}\n  units   ${fmt(units)}\n  ore     ${fmt(ore)}\n  reflect ${fmt(reflection)}\n  bloom   ${fmt(bloom)}\n` +
          `  rest    ${fmt(rest)}\n  total   ${fmt(total)}\n` +
          `res ${rs.toFixed(2)}× · ${bw}×${bh}${this.drs ? " (auto)" : ""}\n` +
          `CPU ${cpu.toFixed(2)} ms · ${fps.toFixed(0)} fps`,
        );
        this.perfLast = t; this.perfFrames = 0; this.perfDt = 0; this.perfCpu = 0;
      }
    }
    requestAnimationFrame(this.frame);
  };
}

/** Numeric invariant sweep across many seeds x levels; logs a summary + any failures. */
function runSweep(levels = 15, seedsPerLevel = 40): void {
  const t0 = performance.now();
  let total = 0;
  let failed = 0;
  let earlyStops = 0;
  const failures: string[] = [];
  let unsolvable = 0;
  let curbBlocked = 0;
  for (let level = 1; level <= levels; level++) {
    for (let i = 0; i < seedsPerLevel; i++) {
      const seed = `sweep-${i}`;
      const lvl = generateLevel(seed, level);
      const r = validateLevel(lvl);
      total++;
      if (!lvl.reachedTarget) earlyStops++;
      if (!r.solvable || !r.fullyTraversable) unsolvable++;
      if (r.curbBlockedTransitions > 0) curbBlocked++;
      if (!r.ok) {
        failed++;
        if (failures.length < 12) failures.push(`L${level} "${seed}": ${r.errors.join("; ")}`);
      }
    }
  }
  const ms = Math.round(performance.now() - t0);
  // eslint-disable-next-line no-console
  console.log(
    `[longfight sweep] ${total} levels (1..${levels} x ${seedsPerLevel} seeds) in ${ms}ms\n` +
      `  invalid: ${failed}\n` +
      `  UNSOLVABLE (player can't reach exit / dead pockets): ${unsolvable}\n` +
      `  levels with a curb blocking a slope: ${curbBlocked}\n` +
      `  early stops (< target tiles): ${earlyStops}`,
    failed ? failures : "all valid",
  );
}

(globalThis as unknown as { lf: unknown }).lf = { generateLevel, validateLevel };
(globalThis as unknown as { __terra: unknown }).__terra = { generateTerra }; // terra sweep validation hook
(globalThis as unknown as { sfx: unknown }).sfx = sfx;
(globalThis as unknown as { music: unknown }).music = music;
(globalThis as unknown as { balance: unknown }).balance = balance;

// M mutes/unmutes all audio (battle sfx + music)
window.addEventListener("keydown", (e) => {
  if ((e.key === "m" || e.key === "M") && !e.repeat) music.setMuted(sfx.toggleMute());
});

const lab = new MapLab(document.getElementById("app")!);
(globalThis as unknown as { __lab: unknown }).__lab = lab;
